import React, { useEffect, useState } from 'react';

import { ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { YEAR_OPTIONS } from 'shared/constants/options';

type BirthdaySelectProps = {
  /** 'YYYY-MM-DD' — 아직 다 고르지 않았으면 '' */
  value: string;
  onChange: (value: string) => void;
  isError?: boolean;
  /** 기본 테두리 색 (에러일 때는 빨간 테두리) */
  borderClassName?: string;
};

type BirthdayParts = { year: string; month: string; day: string };

const toParts = (value: string): BirthdayParts => {
  const [year = '', month = '', day = ''] = value ? value.split('-') : [];
  return { year, month, day };
};

const toValue = ({ year, month, day }: BirthdayParts) => (year && month && day ? `${year}-${month}-${day}` : '');

const pad = (num: number) => String(num).padStart(2, '0');

const MONTHS = Array.from({ length: 12 }, (_, i) => pad(i + 1));

// 연도를 아직 안 골랐으면 윤년(2000) 기준 — 2월 29일까지 보여준다
const getDaysInMonth = ({ year, month }: BirthdayParts) =>
  month ? new Date(Number(year || 2000), Number(month), 0).getDate() : 31;

/** 연/월/일을 각각 고르는 생년월일 입력. 월은 이름으로 보여줘 일·월 순서 혼동을 막는다 */
export const BirthdaySelect = ({
  value,
  onChange,
  isError = false,
  borderClassName = 'border-gray-30',
}: BirthdaySelectProps) => {
  const { t, i18n } = useTranslation('common');
  const [parts, setParts] = useState(() => toParts(value));

  // 프로필 불러오기 등으로 바깥 값이 바뀌면 맞춘다
  useEffect(() => {
    if (value && value !== toValue(parts)) setParts(toParts(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // 예전 달력으로 저장된 범위 밖 연도도 빈칸으로 보이지 않게 목록에 넣는다
  const years = !parts.year || YEAR_OPTIONS.includes(parts.year) ? YEAR_OPTIONS : [parts.year, ...YEAR_OPTIONS];
  // 고른 연·월에 없는 날(4월 31일, 평년 2월 29일)은 목록에서 빠진다
  const days = Array.from({ length: getDaysInMonth(parts) }, (_, i) => pad(i + 1));
  const monthFormatter = new Intl.DateTimeFormat(i18n.language, { month: 'long' });

  const handleChange = (key: keyof BirthdayParts, nextValue: string) => {
    const next = { ...parts, [key]: nextValue };
    if (Number(next.day) > getDaysInMonth(next)) next.day = '';
    setParts(next);
    onChange(toValue(next));
  };

  const selects: { key: keyof BirthdayParts; placeholder: string; options: { value: string; label: string }[] }[] = [
    { key: 'year', placeholder: t('birthdaySelect.year'), options: years.map((y) => ({ value: y, label: y })) },
    {
      key: 'month',
      placeholder: t('birthdaySelect.month'),
      options: MONTHS.map((m) => ({ value: m, label: monthFormatter.format(new Date(2000, Number(m) - 1, 1)) })),
    },
    {
      key: 'day',
      placeholder: t('birthdaySelect.day'),
      options: days.map((d) => ({ value: d, label: String(Number(d)) })),
    },
  ];

  return (
    <div className="grid w-full grid-cols-[1fr_1.4fr_1fr] gap-x-2">
      {selects.map(({ key, placeholder, options }) => (
        <div key={key} className="relative">
          <select
            aria-label={placeholder}
            value={parts[key]}
            onChange={(e) => handleChange(key, e.target.value)}
            className={`h-11 w-full cursor-pointer appearance-none rounded border bg-white py-2.5 pl-2.5 pr-7 text-c4 outline-none ${
              parts[key] ? 'text-black' : 'text-gray-30'
            } ${isError ? 'border-error' : borderClassName}`}
          >
            <option value="" disabled>
              {placeholder}
            </option>
            {options.map((option) => (
              <option key={option.value} value={option.value} className="text-black">
                {option.label}
              </option>
            ))}
          </select>
          <ChevronDown
            size={16}
            className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[#ACACAC]"
            aria-hidden="true"
          />
        </div>
      ))}
    </div>
  );
};
